import { ToQuerySimple } from '@sprinx/query-builder';
import React, { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useHistory } from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';
import { FilterField } from '@sprinx/query-builder';
import { ProductParameterGroupTypes } from '../../@sprinx/knihovka-types';
import {
  AfterPageProps,
  createApiClient,
  createInitialPropsRedirectToLogin,
  makeAfterComponent,
  wrapLayout,
} from '../../@sprinx/react-after-razzle';
import { createListInitialSettings } from '../../@sprinx/react-after-razzle/filteredLists';
import commonThingsToLoad from '../../api/commonThingsToLoad';
import {
  catalogueCallParamsState,
  catalogueInitialState,
  convertUrlToFilterField,
  getProductCatalogue,
} from '../../api/products/catalogue';
import {
  getProductParameterGroupTypes,
  productParameterGroupTypesInitialState,
} from '../../api/products/productParameterGroupTypes';
import ErrorInternal from '../../components/ErrorInternal';
import CartAsideLayout from '../../layouts/CartAsideLayout';
import { getShippings, shippingsState } from '../../api/shipping/shipping';
import { shoppingCustomerSelector } from '../../api/shopping';
import { localeState, useApiClient } from '../../api/appState';
import { shoppingCartState } from '../../api/shoppingCart';
import { getOrderCallParam, getSearchCallParam, getSkuCallParam } from '../../helpers/catalogueParamsFromUrl';
import { catalogueQuery } from '../../api/products/catalogue';
import CataloguePageLayout from './CataloguePageLayout';

type CataloguePageProps = AfterPageProps;

const CataloguePage = React.forwardRef<any, CataloguePageProps>(({ error, match, location }, ref) => {
  const setCallParams = useSetRecoilState(catalogueCallParamsState);
  const customer = useRecoilValue(shoppingCustomerSelector);
  const apiClient = useApiClient();
  const setShippings = useSetRecoilState(shippingsState);
  const cart = useRecoilValue(shoppingCartState);
  const locale = useRecoilValue(localeState);

  const history = useHistory();
  const prevLocationRef = useRef(location);
  const catalogueLoadable = useRecoilValueLoadable(catalogueQuery);
  const prevIsNovinkyRef = useRef<boolean>(false);

  // Prevents need to double click back button
  useEffect(() => {
    return history.listen((location) => {
      if (prevLocationRef.current?.key === location.key) history.goBack();
      prevLocationRef.current = location;
    });
  }, []);

  React.useEffect(() => {
    if (customer && cart)
      getShippings(apiClient, customer, cart.id).then((response) => {
        if (response?.rows.length) setShippings(response.rows);
      });
  }, [cart?.id]);

  React.useEffect(() => {
    if (catalogueLoadable.state !== 'hasValue') return;

    const scrollY = sessionStorage.getItem('catalogueScrollY');
    if (!scrollY) return;

    let raf = 0;
    const start = performance.now();
    const TIMEOUT = 3000;

    const tryScroll = () => {
      const y = Number(scrollY);

      // check that scrolling is actually possible
      if (document.documentElement.scrollHeight > y) {
        window.scrollTo({ top: y, behavior: 'auto' });
        sessionStorage.removeItem('catalogueScrollY');
        return;
      }

      if (performance.now() - start > TIMEOUT) {
        // console.warn('[ScrollRestore] gave up');
        sessionStorage.removeItem('catalogueScrollY');
        return;
      }

      raf = requestAnimationFrame(tryScroll);
    };

    raf = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(raf);
  }, [catalogueLoadable.state, location?.key]);

  React.useEffect(() => {
    const isNovinky = match.url.includes('/novinky');

    const parsedQuery = location?.search
      ? (() => {
          const params = new URLSearchParams(location.search);
          const result: Record<string, string> = {};

          params.forEach((value, key) => {
            result[key] = value;
          });

          return result;
        })()
      : undefined;

    setCallParams((prev) => {
      const search = parsedQuery
        ? (() => {
            const s = getSearchCallParam(parsedQuery, locale);
            if (!s) return undefined;

            return {
              ...s,
              toQuery: (v: string) => {
                const q = s.toQuery(v);
                return q ?? {};
              },
            } as FilterField<any>;
          })()
        : undefined;

      const sku = parsedQuery
        ? (() => {
            const s = getSkuCallParam(parsedQuery);
            if (!s) return undefined;

            return {
              ...s,
              toQuery: (v: string) => {
                const q = s.toQuery(v);
                return q ?? {};
              },
            } as FilterField<any>;
          })()
        : undefined;

      const order =
        parsedQuery?.orderBy && parsedQuery?.orderDirection
          ? {
              orderBy: parsedQuery.orderBy,
              orderDirection: parsedQuery.orderDirection as 'asc' | 'desc',
            }
          : undefined;
      const txff = convertUrlToFilterField(match.url);

      const taxonomies = typeof txff === 'function' ? txff()?.taxonomies : undefined;

      const prevTaxonomiesFns = prev.filter.filter((ff) => typeof ff === 'function') as ToQuerySimple[];

      // the last taxonomy filter is the one actually applied to the query, earlier ones are overwritten by it
      const prevTaxonomiesFn = prevTaxonomiesFns[prevTaxonomiesFns.length - 1];

      const prevTaxonomies = prevTaxonomiesFn ? prevTaxonomiesFn()?.taxonomies : undefined;

      const taxonomyIsSame = taxonomies && prevTaxonomies && taxonomies === prevTaxonomies;

      // keep exactly one taxonomy filter - the one belonging to the current url
      const buildFilter: FilterField<any>[] = [txff as FilterField<any>];

      const extraTaxonomies = prev.filter.filter((ff: any) => ff?.field === 'extraTaxonomies') as FilterField<any>[];

      if (extraTaxonomies.length) {
        buildFilter.push(...extraTaxonomies);
      }

      if (sku) buildFilter.push(sku);
      else if (search) buildFilter.push(search);

      let nextOrder = order ?? prev.order;

      // leaving novinky → reset sorting
      if (!isNovinky && nextOrder?.orderBy === 'lastWarehouseReceipt') {
        nextOrder = {
          orderBy: ['displayName', locale].join('.'),
          orderDirection: 'asc',
        };
      }

      // reset page whenever order changes
      const orderChanged = JSON.stringify(prev.order) !== JSON.stringify(nextOrder);

      return {
        ...prev,
        order: nextOrder,
        filter: buildFilter,
        pagination: orderChanged
          ? {
              ...(prev.pagination || {}),
              page: 1,
            }
          : taxonomyIsSame
          ? { ...prev.pagination }
          : {
              ...(prev.pagination || {}),
              page: 1,
            },
      };
    });

    prevIsNovinkyRef.current = isNovinky;
  }, [match.url, location?.search, locale, setCallParams]);

  if (error) {
    return <ErrorInternal error={error} />;
  }

  return <CataloguePageLayout ref={ref} />;
});

CataloguePage.displayName = 'CataloguePage';

export default makeAfterComponent(
  wrapLayout(CataloguePage, CartAsideLayout),
  async ({ match, location, initialAppProps, ...rest }) => {
    try {
      if (initialAppProps) {
        const appProps = initialAppProps;

        const apiClient = createApiClient({ accessToken: appProps.auth?.accessToken });

        const [parameterGroupTypes, ...commonThings] = (await Promise.all([
          getProductParameterGroupTypes(apiClient),
          ...commonThingsToLoad.promises({ apiClient }),
        ])) as [ProductParameterGroupTypes];

        const query = rest.req?.query;
        const search = query ? getSearchCallParam(query as Record<string, string>, appProps.locale) : undefined;
        const sku = query ? getSkuCallParam(query as Record<string, string>) : undefined;
        const order = query ? getOrderCallParam(query as Record<string, string>) : undefined;
        const params = listInitialSettings(appProps.locale, match.url);

        const fullParams = {
          ...params,
          order: order ? order : params.order,
          filter: sku ? [...params.filter, sku] : search ? [...params.filter, search] : params.filter,
        } as typeof params;

        const initialCatalogueState = await getProductCatalogue(
          apiClient,
          appProps.locale,
          appProps.currency,
          undefined, // decimalPrecision,
          undefined, // enabledDynamicRules,
          appProps.pricesType,
          appProps.region,
          fullParams,
        );

        appProps.propagateToState({
          ...commonThingsToLoad.toRegister(commonThings),
          [catalogueInitialState.key]: initialCatalogueState,
          [productParameterGroupTypesInitialState.key]: parameterGroupTypes,
          [catalogueCallParamsState.key]: fullParams,
        });

        // appProps.presaveState([catalogueCallParamsState.key]);
      }
      return {};
    } catch (error) {
      if (error.response?.status === 401) {
        return createInitialPropsRedirectToLogin({ match });
      }
      return { error };
    }
  },
);

const listInitialSettings = (locale: string, path: string) => {
  const txFilterField = convertUrlToFilterField(path);

  return createListInitialSettings({
    orderBy: ['displayName', locale].join('.'),
    filter: [txFilterField],
  });
};
